import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  AlertCircle,
  Activity,
  RefreshCw,
  DollarSign,
  Clock,
  AlertTriangle,
  CheckSquare,
  GitBranch,
  Lightbulb,
  Lock,
  Users,
  Link,
  HelpCircle,
  CheckCircle,
  TrendingUp,
  MessageCircle,
  ExternalLink,
  X,
  Plus,
  Briefcase,
  Target,
  Sparkles,
  BarChart,
  type LucideIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Facet, FacetType } from '@/types/interviewer'
import { FACET_TYPE_CONFIG } from '@/types/interviewer'

// Icon mapping for facet types
const ICON_MAP: Record<string, LucideIcon> = {
  AlertCircle,
  Activity,
  RefreshCw,
  DollarSign,
  Clock,
  AlertTriangle,
  CheckSquare,
  GitBranch,
  Lightbulb,
  Lock,
  Users,
  Link,
  HelpCircle,
  CheckCircle,
  TrendingUp,
  MessageCircle,
  Briefcase,
  Target,
  Sparkles,
  BarChart,
}

function getIcon(facetType: FacetType): LucideIcon {
  const config = FACET_TYPE_CONFIG[facetType]
  return ICON_MAP[config?.icon] || AlertCircle
}

function getColor(facetType: FacetType): string {
  return FACET_TYPE_CONFIG[facetType]?.color || '#6c757d'
}

function getLabel(facetType: FacetType): string {
  return FACET_TYPE_CONFIG[facetType]?.label || facetType.replace(/_/g, ' ')
}

interface FacetCardProps {
  facet: Facet
  isSelected: boolean
  onClick: () => void
  onDismiss?: (reason: string) => void
  onLinkToOntology?: () => void
}

export function FacetCard({
  facet,
  isSelected,
  onClick,
  onDismiss,
  onLinkToOntology,
}: FacetCardProps) {
  const Icon = getIcon(facet.type)
  const color = getColor(facet.type)
  const label = getLabel(facet.type)
  const config = FACET_TYPE_CONFIG[facet.type]

  return (
    <Card
      className={cn(
        'p-3 cursor-pointer transition-all',
        isSelected ? 'ring-2 ring-solution' : 'hover:bg-warm-50',
        facet.dismissed && 'opacity-50'
      )}
      onClick={onClick}
    >
      {/* Header */}
      <div className="flex items-start gap-2 mb-2">
        <div
          className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${color}20` }}
        >
          <Icon className="w-3.5 h-3.5" style={{ color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className="text-xs"
              style={{ borderColor: color, color }}
            >
              {label}
            </Badge>
            <span className="text-xs text-text-muted">
              {Math.round(facet.confidence * 100)}% confidence
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <p className="text-sm mb-2">{facet.content}</p>

      {/* Evidence quote */}
      <div className="bg-warm-100 rounded p-2 text-xs text-text-secondary italic border-l-2"
        style={{ borderColor: color }}
      >
        "{facet.evidence}"
      </div>

      {/* Metadata badges */}
      {(facet.severity || facet.frequency || facet.decisionType || facet.misalignmentPattern || facet.evidenceStrength) && (
        <div className="flex gap-2 mt-2 flex-wrap">
          {facet.severity && (
            <Badge variant="secondary" className="text-xs capitalize">
              {facet.severity} severity
            </Badge>
          )}
          {facet.frequency && (
            <Badge variant="secondary" className="text-xs capitalize">
              {facet.frequency}
            </Badge>
          )}
          {facet.evidenceStrength && (
            <Badge variant="secondary" className="text-xs">
              Evidence: {facet.evidenceStrength}/5
            </Badge>
          )}
          {facet.decisionType && (
            <Badge variant="secondary" className="text-xs capitalize">
              {facet.decisionType}
            </Badge>
          )}
          {facet.reversibility && (
            <Badge
              variant="secondary"
              className={cn(
                'text-xs',
                facet.reversibility === 'one-way-door' && 'bg-problem/10 text-problem'
              )}
            >
              {facet.reversibility === 'one-way-door' ? 'One-way door' : 'Two-way door'}
            </Badge>
          )}
          {facet.misalignmentPattern && (
            <Badge variant="secondary" className="text-xs capitalize">
              {facet.misalignmentPattern}
            </Badge>
          )}
        </div>
      )}

      {/* Suggested ontology mapping */}
      {facet.suggestedOntologyType && facet.ontologyLinks.length === 0 && (
        <div className="mt-2 pt-2 border-t border-warm-200">
          <div className="flex items-center gap-1 text-xs text-solution">
            <Sparkles className="w-3 h-3" />
            Suggested: {facet.suggestedOntologyType}
            {facet.suggestedOntologyReason && (
              <span className="text-text-muted ml-1">({facet.suggestedOntologyReason})</span>
            )}
          </div>
        </div>
      )}

      {/* Ontology links */}
      {facet.ontologyLinks.length > 0 && (
        <div className="mt-2 pt-2 border-t border-warm-200">
          <div className="flex items-center gap-1 text-xs text-text-muted mb-1">
            <Link className="w-3 h-3" />
            Linked to ontology
          </div>
          <div className="flex gap-1 flex-wrap">
            {facet.ontologyLinks.map((link) => (
              <Badge
                key={link.id}
                variant="outline"
                className="text-xs bg-solution/5 border-solution/30"
              >
                {link.entityLabel}
                <ExternalLink className="w-2.5 h-2.5 ml-1" />
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Ontology type hint from config */}
      {config?.ontologyTypes && config.ontologyTypes.length > 0 && facet.ontologyLinks.length === 0 && !facet.suggestedOntologyType && (
        <div className="mt-2 pt-2 border-t border-warm-200">
          <div className="flex items-center gap-1 text-xs text-text-muted">
            <Link className="w-3 h-3" />
            Maps to: {config.ontologyTypes.join(', ')}
          </div>
        </div>
      )}

      {/* Actions (when selected) */}
      {isSelected && !facet.dismissed && (
        <div className="mt-3 pt-2 border-t border-warm-200 flex gap-2">
          {onLinkToOntology && facet.ontologyLinks.length === 0 && (
            <Button
              size="sm"
              variant="outline"
              className="text-xs h-7 gap-1"
              onClick={(e) => {
                e.stopPropagation()
                onLinkToOntology()
              }}
            >
              <Plus className="w-3 h-3" />
              Link to Ontology
            </Button>
          )}
          {onDismiss && (
            <Button
              size="sm"
              variant="ghost"
              className="text-xs h-7 gap-1 text-text-muted"
              onClick={(e) => {
                e.stopPropagation()
                onDismiss('User dismissed')
              }}
            >
              <X className="w-3 h-3" />
              Dismiss
            </Button>
          )}
        </div>
      )}
    </Card>
  )
}
